import { useCallback } from 'react';
import { listUsers, createUser, updateUserRoles, deleteUser } from '../lib/apiClient';
import { useDataFetching } from './useDataFetching';
import type { PlatformUser, CreatePlatformUserInput, UpdateUserRolesInput } from '../types/users';

interface UseUsersResult {
  users: PlatformUser[];
  loading: boolean;
  error: string | null;
  createUser: (input: CreatePlatformUserInput) => Promise<PlatformUser>;
  updateRoles: (userId: string, input: UpdateUserRolesInput) => Promise<PlatformUser>;
  deleteUser: (userId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useUsers(): UseUsersResult {
  const { items, setItems, loading, error, refetch, create, remove } = useDataFetching<
    PlatformUser,
    CreatePlatformUserInput,
    PlatformUser
  >({
    fetchFn: listUsers,
    createFn: createUser,
    selectCreated: (user) => user,
    deleteFn: deleteUser,
    getItemId: (user) => user.userId,
  });

  const handleUpdateRoles = useCallback(
    async (userId: string, input: UpdateUserRolesInput): Promise<PlatformUser> => {
      const updated = await updateUserRoles(userId, input);
      setItems((prev) => prev.map((u) => (u.userId === userId ? updated : u)));
      return updated;
    },
    [setItems]
  );

  return {
    users: items,
    loading,
    error,
    createUser: create,
    updateRoles: handleUpdateRoles,
    deleteUser: remove,
    refetch,
  };
}
